import { Link } from 'react-router-dom';
import {
  Building2,
  Users,
  AlertTriangle,
  Clock,
  Activity,
  ArrowRight,
  RefreshCw,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { Skeleton } from '@/app/components/ui/skeleton';
import { usePlatformStats, useExtendTrial, usePlatformOrganizations, useSyncAllModules, usePlatformOrgActivityTable } from '@/hooks/use-platform';
import { toast } from '@/app/components/ui/simple-toaster';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';

function OrgActivityTable() {
  const { data, isLoading, isError, refetch } = usePlatformOrgActivityTable();
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Org activity</CardTitle>
        <CardDescription>Customer activity, most recent first. Platform staff and system events are excluded.</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading && <Skeleton className="h-32 w-full" />}
        {isError && <p role="alert" className="text-sm">Unable to load organization activity. <Button variant="outline" size="sm" onClick={() => refetch()}>Retry</Button></p>}
        {data && <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead><tr className="border-b text-gray-500"><th className="pb-3">Organization</th><th className="pb-3">Last activity (org time)</th><th className="pb-3 text-right">Active users · 7 days</th></tr></thead>
            <tbody>{data.organizations.map((org) => (
              <tr key={org.organization_id} className="border-b last:border-0">
                <td className="py-3"><Link className="font-medium text-blue-600 hover:underline" to={`/platform-admin/organizations/${org.organization_id}`}>{org.organization_name}</Link>{org.is_demo && <Badge variant="outline" className="ml-2">Demo</Badge>}</td>
                <td className="py-3">{org.last_activity ? formatDateTimeInTimeZone(org.last_activity, org.time_zone) : 'No activity yet'}<span className="block text-xs text-gray-500">{org.time_zone}</span></td>
                <td className="py-3 text-right">{org.active_users_7d}</td>
              </tr>
            ))}</tbody>
          </table>
          {data.organizations.length === 0 && <p className="py-4 text-gray-500">No organizations yet.</p>}
        </div>}
      </CardContent>
    </Card>
  );
}

function StatCardSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-2">
        <Skeleton className="h-4 w-24" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-8 w-16" />
        <Skeleton className="mt-2 h-3 w-32" />
      </CardContent>
    </Card>
  );
}

const MODULE_LABELS: Record<string, { label: string; color: string }> = {
  inventory: { label: 'H3Gear', color: 'bg-blue-100 text-blue-800' },
  safety: { label: 'H3Safe', color: 'bg-green-100 text-green-800' },
  crew: { label: 'H3Crew', color: 'bg-purple-100 text-purple-800' },
  projects: { label: 'H3Books', color: 'bg-amber-100 text-amber-800' },
  payroll: { label: 'H3Pay', color: 'bg-emerald-100 text-emerald-800' },
  compliance: { label: 'H3Comply', color: 'bg-rose-100 text-rose-800' },
};

export function PlatformAdminDashboardPage() {
  const { data: stats, isLoading, isError, refetch } = usePlatformStats();
  const { data: expiringOrgs } = usePlatformOrganizations(
    stats?.expiring_trials && stats.expiring_trials > 0
      ? { trial_expiring_within: '7' }
      : undefined
  );
  const extendTrial = useExtendTrial();
  const syncAll = useSyncAllModules();

  const handleSyncAll = () => {
    syncAll.mutate(undefined, {
      onSuccess: (data) => {
        toast.success(`Synced modules for ${data.synced} org(s).`);
      },
      onError: () => {
        toast.error('Failed to sync modules. Please try again.');
      },
    });
  };

  const handleExtendTrial = (orgId: number, orgName: string) => {
    extendTrial.mutate(
      { orgId, trial_days: 14 },
      {
        onSuccess: () => {
          toast.success(`Extended trial for ${orgName} by 14 days.`);
        },
        onError: () => {
          toast.error('Failed to extend trial. Please try again.');
        },
      }
    );
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Platform Dashboard</h1>
        <p className="text-gray-600 mt-1">Overview of all organizations and platform usage.</p>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCardSkeleton />
          <StatCardSkeleton />
          <StatCardSkeleton />
          <StatCardSkeleton />
        </div>
      ) : isError ? (
        <Card>
          <CardContent className="py-12 text-center">
            <AlertTriangle className="h-10 w-10 text-orange-500 mx-auto mb-3" />
            <p className="text-gray-900 font-medium">Unable to load platform stats</p>
            <p className="text-sm text-gray-500 mt-1">Something went wrong while fetching data.</p>
            <Button variant="outline" size="sm" className="mt-4" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : stats ? (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Total Organizations</CardTitle>
                <Building2 className="h-4 w-4 text-gray-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.total_orgs}</div>
                <p className="text-xs text-gray-500 mt-1">
                  {stats.active_orgs} active
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Active Organizations</CardTitle>
                <Activity className="h-4 w-4 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{stats.active_orgs}</div>
                <p className="text-xs text-gray-500 mt-1">
                  {stats.total_orgs > 0
                    ? `${Math.round((stats.active_orgs / stats.total_orgs) * 100)}% of total`
                    : 'No organizations yet'}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Trials Expiring</CardTitle>
                <Clock className="h-4 w-4 text-orange-500" />
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${stats.expiring_trials > 0 ? 'text-orange-600' : ''}`}>
                  {stats.expiring_trials}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {stats.trial_orgs} total trials
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Total Users</CardTitle>
                <Users className="h-4 w-4 text-blue-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.total_users}</div>
                <p className="text-xs text-gray-500 mt-1">
                  {stats.active_users_30d} active in last 30d
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Module Adoption */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Module Adoption</CardTitle>
                  <CardDescription>Number of organizations using each module</CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSyncAll}
                  disabled={syncAll.isPending}
                >
                  <RefreshCw className={`h-4 w-4 mr-1 ${syncAll.isPending ? 'animate-spin' : ''}`} />
                  Sync All
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {Object.keys(stats.modules_breakdown).length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(stats.modules_breakdown).map(([key, count]) => {
                    const moduleInfo = MODULE_LABELS[key] || { label: key, color: 'bg-gray-100 text-gray-800' };
                    const percentage = stats.total_orgs > 0 ? Math.round((count / stats.total_orgs) * 100) : 0;
                    return (
                      <div key={key} className="flex items-center gap-4">
                        <Badge className={`${moduleInfo.color} min-w-[80px] justify-center`}>
                          {moduleInfo.label}
                        </Badge>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm text-gray-600">{count} orgs</span>
                            <span className="text-sm text-gray-400">{percentage}%</span>
                          </div>
                          <div className="h-2 rounded-full bg-gray-100">
                            <div
                              className="h-2 rounded-full bg-blue-500 transition-all"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-4">No module data available</p>
              )}
            </CardContent>
          </Card>

          {/* Trials Expiring Soon — only show when there are expiring trials */}
          {stats.expiring_trials > 0 && expiringOrgs && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-orange-500" />
                      Trials Expiring Soon
                    </CardTitle>
                    <CardDescription>Organizations with trials expiring within 7 days</CardDescription>
                  </div>
                  <Button asChild variant="outline" size="sm">
                    <Link to="/platform-admin/organizations">
                      View All
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {expiringOrgs.results.length > 0 ? (
                  <div className="space-y-3">
                    {expiringOrgs.results.slice(0, 5).map((org) => (
                      <div
                        key={org.id}
                        className="flex items-center justify-between p-3 rounded-lg bg-orange-50 border border-orange-100"
                      >
                        <div>
                          <p className="text-sm font-medium text-gray-900">{org.name}</p>
                          <p className="text-xs text-gray-500">{org.admin_email ?? 'No admin email'}</p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleExtendTrial(org.id, org.name)}
                          disabled={extendTrial.isPending}
                        >
                          Extend 14d
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-4">No trials expiring soon</p>
                )}
              </CardContent>
            </Card>
          )}
        </>
      ) : null}
      <OrgActivityTable />
    </div>
  );
}
